import { type ReactNode, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { router } from '@/core/router';
import { useAuthStore } from '@/stores/auth';
import { ToastProvider } from '@/components/ui/toast';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useAuthFocusRefresh } from '@/hooks/use-auth-focus-refresh';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

function AuthInit({ children }: { children: ReactNode }): ReactNode {
  const hydrate = useAuthStore((s) => s.hydrate);
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const isLoading = useAuthStore((s) => s.isLoading);

  useEffect(() => {
    hydrate();
    refreshAuth();
  }, [hydrate, refreshAuth]);

  useDocumentTitle();
  useAuthFocusRefresh();
  if (isLoading) return null;
  return <>{children}</>;
}

export function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthInit>
          <RouterProvider router={router} />
        </AuthInit>
      </ToastProvider>
    </QueryClientProvider>
  );
}
